import { Skeleton } from "antd";
import React from "react";
import { formatDateTime } from "admin/Authentication/hooks/useDateFormat";

type TInvitationDate = {
  id: number;
  date: string;
};

type TInvitation = {
  id: number;
  listing_id: string;
  listing_title: string;
  status: 0 | 1 | 2;
  status_label: "pending" | "accepted" | "rejected";
  note: string | null;
  meeting_platform: string | null;
  meeting_link: string | null;
  start_date: string | null;
  corporate_id: number | null;
  dates: TInvitationDate[];
  created_at: string;
};

export const Invite: React.FC<{
  invitations?: TInvitation[];
  isLoading?: boolean;
}> = ({ invitations = [], isLoading }) => {
  if (!invitations.length && !isLoading) {
    return (
      <>
        <h3 className="font-semibold text-base text-gray-800 mb-4">Invites</h3>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-sm text-gray-500">No invites for this talent.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <h3 className="font-semibold text-base text-gray-800 mb-4">Invites</h3>

      <Skeleton active loading={isLoading}>
        <div className="space-y-4">
          {invitations.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-gray-200 rounded-xl p-5"
            >
              <div className="space-y-1.5">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-gray-800">Role:</span>{" "}
                  {item.listing_title}
                </p>

                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-gray-800">Status:</span>{" "}
                  <span className={statusColor(item.status)}>
                    {item.status_label}
                  </span>
                </p>

                {item.note && (
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-gray-800">Note:</span>{" "}
                    {item.note}
                  </p>
                )}

                {item.meeting_platform && (
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-gray-800">
                      Meeting:
                    </span>{" "}
                    {item.meeting_platform}
                    {item.meeting_link && (
                      <>
                        {" — "}
                        <a
                          href={item.meeting_link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline"
                        >
                          Join link
                        </a>
                      </>
                    )}
                  </p>
                )}

                {item.dates?.length > 0 && (
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-gray-800">
                      Proposed date{item.dates.length > 1 ? "s" : ""}:
                    </span>{" "}
                    {item.dates
                      .map((d) => formatDateTime(d.date))
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}

                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-gray-800">Sent:</span>{" "}
                  {formatDateTime(item.created_at)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Skeleton>
    </>
  );
};

const statusColor = (status: number) => {
  switch (status) {
    case 1:
      return "text-green-600 font-medium";
    case 2:
      return "text-red-600 font-medium";
    default:
      return "text-amber-600 font-medium";
  }
};
